import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { USER_COOKIE_CONSENT } from '../shared/constants/storage-keys.constants';
import { safeStorageGet, safeStorageSet } from '../shared/utils/safe-storage';

export type ConsentCategory = 'essential' | 'analytics' | 'sessionRecording';

export interface ConsentState {
  essential: true;
  analytics: boolean;
  sessionRecording: boolean;
  version: 1;
  decidedAt: string;
}

const CONSENT_VERSION = 1;

const DAY_MS = 24 * 60 * 60 * 1000;
// Renewal windows align with ANPD 2023 cookie guidance and common DPO
// frameworks (CNIL, ICO): session-recording is the most invasive and
// renews twice as fast as plain analytics.
const SESSION_RECORDING_MAX_AGE_MS = 180 * DAY_MS;
const ANALYTICS_MAX_AGE_MS = 365 * DAY_MS;

@Injectable({ providedIn: 'root' })
export class ConsentService implements OnDestroy {
  private readonly subject = new BehaviorSubject<ConsentState | null>(null);

  readonly consent$: Observable<ConsentState | null> =
    this.subject.asObservable();

  // Emits when the user asks to review their choices again (e.g. via a
  // "Gerenciar cookies" control). Lets the banner reopen without wiping
  // the stored decision — LGPD requires consent to be revocable at any
  // time, and there is no other in-app way to change it after deciding.
  private readonly reopenSubject = new Subject<void>();
  readonly reopen$: Observable<void> = this.reopenSubject.asObservable();

  constructor(private ngZone: NgZone) {
    this.subject.next(this.load());
    window.addEventListener('storage', this.handleStorageEvent);
    // Re-evaluate when the tab regains focus so a long-lived session that
    // crossed the renewal window (180d session-recording / 365d analytics)
    // expires immediately, instead of waiting for the next page reload.
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.handleStorageEvent);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
  }

  get hasDecided(): boolean {
    return this.subject.value !== null;
  }

  has(category: ConsentCategory): boolean {
    const state = this.subject.value;
    return !!state && state[category] === true;
  }

  category$(category: ConsentCategory): Observable<boolean> {
    return this.consent$.pipe(
      map((state) => !!state && state[category] === true),
      distinctUntilChanged()
    );
  }

  accept(categories: Exclude<ConsentCategory, 'essential'>[]): void {
    this.persist({
      essential: true,
      analytics: categories.includes('analytics'),
      sessionRecording: categories.includes('sessionRecording'),
      version: CONSENT_VERSION,
      decidedAt: new Date().toISOString(),
    });
  }

  acceptAll(): void {
    this.accept(['analytics', 'sessionRecording']);
  }

  essentialOnly(): void {
    this.accept([]);
  }

  /** Reopen the consent banner so the user can review/revoke their choice. */
  reopen(): void {
    this.reopenSubject.next();
  }

  private persist(state: ConsentState): void {
    safeStorageSet(USER_COOKIE_CONSENT, JSON.stringify(state));
    this.subject.next(state);
  }

  private load(): ConsentState | null {
    const raw = safeStorageGet(USER_COOKIE_CONSENT);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as ConsentState;
        if (
          parsed &&
          parsed.version === CONSENT_VERSION &&
          !this.isExpired(parsed)
        ) {
          // Build the state explicitly rather than spreading `parsed` so
          // tampered or non-boolean values from localStorage cannot leak
          // into the in-memory state. Reads guard with `=== true`, but
          // normalising here keeps the runtime type honest.
          return {
            essential: true,
            analytics: !!parsed.analytics,
            sessionRecording: !!parsed.sessionRecording,
            version: CONSENT_VERSION,
            decidedAt: parsed.decidedAt,
          };
        }
      } catch {
        // fall through — treat as undecided so the banner reappears
      }
      return null;
    }

    // No stored consent — banner must appear. Note: users who only had the
    // pre-DEV-1668 binary `user-accepted-cookies` flag also land here, so
    // they are re-prompted per category. Legacy consent is never presumed
    // for the new, more invasive session-recording category (LGPD: consent
    // must be specific for each purpose).
    return null;
  }

  private isExpired(state: ConsentState): boolean {
    const decidedAtMs = Date.parse(state.decidedAt);
    if (Number.isNaN(decidedAtMs)) return true;
    const ageMs = Date.now() - decidedAtMs;
    // Renewal window scales with invasiveness. sessionRecording is the
    // most invasive category and renews twice as fast as analytics.
    // Essential-only users (sessionRecording=false, analytics=false) fall
    // into the analytics window by design — a yearly re-prompt is the
    // intentional ceiling for the lowest-risk choice.
    const maxAgeMs = state.sessionRecording
      ? SESSION_RECORDING_MAX_AGE_MS
      : ANALYTICS_MAX_AGE_MS;
    return ageMs > maxAgeMs;
  }

  private readonly handleStorageEvent = (event: StorageEvent): void => {
    if (event.key !== USER_COOKIE_CONSENT) return;
    this.ngZone.run(() => {
      this.subject.next(this.load());
    });
  };

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    this.ngZone.run(() => {
      this.subject.next(this.load());
    });
  };
}
